import { useTranslation } from 'react-i18next';
import { trDisplay } from '../../i18n';
import React from 'react';
import { Card, CardContent } from '../ui/card';
import { HelpCircle } from 'lucide-react';
import { MobileTooltip } from '../ui/tooltip';

/**
 * StatCard - Reusable card component for displaying statistics
 * Used across both Credit Lombard and SWR tabs
 */
export const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  variant = 'default',
  tooltip,
  // Allow theming for different contexts
  theme = 'default' // 'default' | 'lombard' | 'swr'
}) => {
  useTranslation();
  const variants = {
    default: 'bg-white border-slate-100',
    success: 'bg-emerald-50 border-emerald-100',
    warning: 'bg-amber-50 border-amber-100',
    danger: 'bg-red-50 border-red-100',
    info: theme === 'swr' ? 'bg-blue-50 border-blue-100' : 'bg-violet-50 border-violet-100',
  };

  const iconVariants = {
    default: 'text-slate-500 bg-slate-100',
    success: 'text-emerald-600 bg-emerald-100',
    warning: 'text-amber-600 bg-amber-100',
    danger: 'text-red-600 bg-red-100',
    info: theme === 'swr' ? 'text-blue-600 bg-blue-100' : 'text-violet-600 bg-violet-100',
  };

  return (
    <Card className={`${variants[variant]} border shadow-sm hover:shadow-md transition-shadow duration-200`}>
      <CardContent className="p-3 sm:p-5 stat-card-content">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1 flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-xs sm:text-sm font-medium text-slate-500 stat-card-title leading-tight">{trDisplay(title)}</p>
              {trDisplay(tooltip && (
                <MobileTooltip
                  content={React.isValidElement(tooltip) ? tooltip : <p className="text-sm">{trDisplay(tooltip)}</p>}
                  side="top"
                >
                  <HelpCircle className="h-3.5 w-3.5 text-slate-400 cursor-help flex-shrink-0" />
                </MobileTooltip>
              ))}
            </div>
            <p className="text-xl sm:text-2xl font-bold text-slate-800 stat-card-value">{trDisplay(value)}</p>
            {trDisplay(subtitle && (
              <p className="text-xs text-slate-400">{trDisplay(subtitle)}</p>
            ))}
          </div>
          <div className={`p-2 sm:p-3 rounded-xl flex-shrink-0 ${iconVariants[variant]}`}>
            <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatCard;
